import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState, type FormEvent, type ReactNode } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  parametrization,
  type ModalityDetail,
  type ModalityPatch,
  type ModalitySetting,
  type ModalitySettingValues,
  type RetroactivityHandling,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import {
  formatDate,
  formatDateTime,
  moneyInputError,
  rateInputError,
  rateToBasisPoints,
  rateToInput,
} from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { DataTable } from '@/shared/ui/DataTable';
import { DetailCard } from '@/shared/ui/DetailLayout';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { FieldLabel } from '@/shared/ui/FieldLabel';
import { Modal } from '@/shared/ui/Modal';
import { MoneyField } from '@/shared/ui/MoneyField';
import { Select } from '@/shared/ui/Select';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../shared/fieldError';
import {
  campoParaDinheiro,
  campoParaInteiro,
  dinheiroParaCampo,
  erroDeDias,
  inteiroParaCampo,
} from './campos';
import { AVISO_AUTOAPROVACAO, autoriaDe } from './autoaprovacao';
import { NomeDaCoberturaImpresso } from './NomeDaCoberturaImpresso';
import { gruposDeCreditoDaModalidade, referenciasDeCredito } from './rotulos';

/** Interruptor do OnPoint: caixa com papel de chave, rotulo e ajuda (i). */
function Interruptor({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (marcado: boolean) => void;
  disabled: boolean;
}) {
  const id = useId();
  return (
    <div className="flex items-center justify-between gap-4 border-b border-line py-2.5 last:border-b-0">
      <FieldLabel htmlFor={id} label={label} className="text-sm text-ink-800" />
      <input
        id={id}
        type="checkbox"
        role="switch"
        aria-checked={checked}
        checked={checked}
        disabled={disabled}
        onChange={(evento) => onChange(evento.target.checked)}
        className="size-4 shrink-0 accent-[var(--color-brand-500)]"
      />
    </div>
  );
}

/** Opcao da "Tratativa de inicio de vigencia". */
function Tratativa({
  label,
  valor,
  atual,
  onChange,
  disabled,
  name,
}: {
  label: string;
  valor: RetroactivityHandling;
  atual: string;
  onChange: (valor: string) => void;
  disabled: boolean;
  name: string;
}) {
  const id = useId();
  return (
    <div className="flex items-center gap-2">
      <input
        id={id}
        type="radio"
        name={name}
        value={valor}
        checked={atual === valor}
        disabled={disabled}
        onChange={() => onChange(valor)}
        className="size-4 accent-[var(--color-brand-500)]"
      />
      <FieldLabel htmlFor={id} label={label} className="text-sm text-ink-800" />
    </div>
  );
}

const valoresVazios: ModalitySettingValues = {
  max_insured_amount: null,
  max_auto_approved_amount: null,
  min_premium: null,
  min_term_days: null,
  max_term_days: null,
  max_future_days: null,
  retroactivity_handling: null,
  max_retroactive_days: null,
  auto_issuance_enabled: false,
  is_complementary: false,
  allows_complementary: false,
  restricted_for_restricted_activity: false,
  equalize_coverage_terms: false,
  allows_individual_insured: false,
  allows_beneficiary: false,
  is_monitored: false,
  allows_step_in: false,
  is_large_risk: false,
  auto_cancel_expired: false,
  modality_rate_enabled: false,
  ignore_branch_for_individual_insured: false,
  auto_cancel_days: null,
  manual_cancel_days: null,
  modality_rate_bps: null,
  accumulation_enabled: false,
  retroactive_pipe_enabled: false,
  is_auctioneer: false,
};

type Interruptores = Pick<
  ModalitySettingValues,
  | 'auto_issuance_enabled'
  | 'is_complementary'
  | 'allows_complementary'
  | 'restricted_for_restricted_activity'
  | 'equalize_coverage_terms'
  | 'allows_individual_insured'
  | 'allows_beneficiary'
  | 'is_monitored'
  | 'allows_step_in'
  | 'is_large_risk'
  | 'auto_cancel_expired'
  | 'modality_rate_enabled'
  | 'ignore_branch_for_individual_insured'
  | 'accumulation_enabled'
  | 'retroactive_pipe_enabled'
  | 'is_auctioneer'
>;

function interruptoresDe(v: ModalitySettingValues): Interruptores {
  return {
    auto_issuance_enabled: v.auto_issuance_enabled,
    is_complementary: v.is_complementary,
    allows_complementary: v.allows_complementary,
    restricted_for_restricted_activity: v.restricted_for_restricted_activity,
    equalize_coverage_terms: v.equalize_coverage_terms,
    allows_individual_insured: v.allows_individual_insured,
    allows_beneficiary: v.allows_beneficiary,
    is_monitored: v.is_monitored,
    allows_step_in: v.allows_step_in,
    is_large_risk: v.is_large_risk,
    auto_cancel_expired: v.auto_cancel_expired,
    modality_rate_enabled: v.modality_rate_enabled,
    ignore_branch_for_individual_insured: v.ignore_branch_for_individual_insured,
    accumulation_enabled: v.accumulation_enabled,
    retroactive_pipe_enabled: v.retroactive_pipe_enabled,
    is_auctioneer: v.is_auctioneer,
  };
}

/** Compara dois valores pelo conteudo, sem depender da ordem das chaves. */
function mesmoConteudo(a: unknown, b: unknown): boolean {
  const canonico = (valor: unknown): unknown => {
    if (Array.isArray(valor)) return valor.map(canonico);
    if (valor && typeof valor === 'object') {
      return Object.fromEntries(
        Object.entries(valor as Record<string, unknown>)
          .sort(([x], [y]) => x.localeCompare(y))
          .map(([chave, item]) => [chave, canonico(item)]),
      );
    }
    return valor;
  };
  return JSON.stringify(canonico(a)) === JSON.stringify(canonico(b));
}

function maisRecente(...datas: (string | null | undefined)[]): string | undefined {
  return datas
    .filter((d): d is string => typeof d === 'string' && d !== '')
    .sort()
    .at(-1);
}

/**
 * Aba "Detalhes da modalidade", com os campos e os treze interruptores na ordem do
 * OnPoint, e a governanca da configuracao.
 *
 * O cadastro (nome, situacao e codigo de referencia) muda na hora, como mudava em
 * /produtos. Os parametros vao para o rascunho da configuracao: "Salvar detalhes"
 * abre o rascunho (copiando a versao em vigor) ou edita o que ja esta aberto, e outra
 * pessoa ativa com justificativa. Nenhum valor daqui decide a operacao ate a
 * homologacao: o motor de credito continua lendo os parametros dele.
 */
export function DetalhesDaModalidade({ detalhe }: { detalhe: ModalityDetail }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const nomeDaTratativa = useId();

  const podeEditarCadastro = can('parametrizacao.modalidade.update');
  const rascunho = detalhe.draft_setting;
  const ativa = detalhe.active_setting;
  const podeEditarParametros = rascunho
    ? can('parametrizacao.modalidade.update')
    : can('parametrizacao.modalidade.create');
  const podeAtivar = can('parametrizacao.modalidade.approve');
  const somenteLeitura = !podeEditarCadastro && !podeEditarParametros;

  const base = rascunho?.values ?? ativa?.values ?? valoresVazios;

  const grupos = useQuery({
    queryKey: ['modality-groups', detalhe.product.id],
    queryFn: ({ signal }) =>
      parametrization.listGroups({ product_id: detalhe.product.id }, signal),
  });

  // Cadastro.
  const [nome, setNome] = useState(detalhe.name);
  const [inativa, setInativa] = useState(detalhe.status === 'inativo');
  // O rascunho da carga com aprovacao (P-CARGA) so sai pela ativacao de outra pessoa:
  // salvar os detalhes mantem a situacao, e o interruptor fica desligado.
  const emRascunho = detalhe.status === 'rascunho';
  const [referencia, setReferencia] = useState(detalhe.insurer_reference_code ?? '');
  const [codigo, setCodigo] = useState(detalhe.code);
  const [descricao, setDescricao] = useState(detalhe.description);
  const [taxaBase, setTaxaBase] = useState(rateToInput(detalhe.base_rate_bps));
  const [grupo, setGrupo] = useState(detalhe.modality_group_id ?? '');
  const [grupoDeCredito, setGrupoDeCredito] = useState<string>(
    detalhe.credit_group ?? '',
  );
  const [referenciaDeCredito, setReferenciaDeCredito] = useState(
    detalhe.credit_reference ?? '',
  );
  const [processo, setProcesso] = useState(detalhe.susep_process_number ?? '');

  // Parametros.
  const [maxSegurado, setMaxSegurado] = useState(
    dinheiroParaCampo(base.max_insured_amount),
  );
  const [maxAutomatico, setMaxAutomatico] = useState(
    dinheiroParaCampo(base.max_auto_approved_amount),
  );
  const [premioMinimo, setPremioMinimo] = useState(dinheiroParaCampo(base.min_premium));
  const [vigenciaMinima, setVigenciaMinima] = useState(
    inteiroParaCampo(base.min_term_days),
  );
  const [vigenciaMaxima, setVigenciaMaxima] = useState(
    inteiroParaCampo(base.max_term_days),
  );
  const [diasFuturos, setDiasFuturos] = useState(
    inteiroParaCampo(base.max_future_days),
  );
  const [tratativa, setTratativa] = useState<string>(base.retroactivity_handling ?? '');
  const [diasRetroativos, setDiasRetroativos] = useState(
    inteiroParaCampo(base.max_retroactive_days),
  );
  const [diasCancelamentoAutomatico, setDiasCancelamentoAutomatico] = useState(
    inteiroParaCampo(base.auto_cancel_days),
  );
  const [diasCancelamentoManual, setDiasCancelamentoManual] = useState(
    inteiroParaCampo(base.manual_cancel_days),
  );
  const [taxaDaModalidade, setTaxaDaModalidade] = useState(
    base.modality_rate_bps === null ? '' : rateToInput(base.modality_rate_bps),
  );
  const [chaves, setChaves] = useState<Interruptores>(interruptoresDe(base));
  const [faltando, setFaltando] = useState<Record<string, string>>({});

  const [ativando, setAtivando] = useState(false);

  function alternar(chave: keyof Interruptores) {
    return (marcado: boolean) =>
      setChaves((atuais) => ({ ...atuais, [chave]: marcado }));
  }

  const erros = {
    maxSegurado: moneyInputError(maxSegurado),
    maxAutomatico: moneyInputError(maxAutomatico),
    premioMinimo: moneyInputError(premioMinimo),
    vigenciaMinima: erroDeDias(vigenciaMinima),
    vigenciaMaxima: erroDeDias(vigenciaMaxima),
    diasFuturos: erroDeDias(diasFuturos) ?? faltando.diasFuturos,
    diasRetroativos: erroDeDias(diasRetroativos) ?? faltando.diasRetroativos,
    diasCancelamentoAutomatico: erroDeDias(diasCancelamentoAutomatico),
    diasCancelamentoManual: erroDeDias(diasCancelamentoManual),
    taxaDaModalidade: rateInputError(taxaDaModalidade),
    taxaBase: rateInputError(taxaBase),
  };
  const temErro = Object.values(erros).some((erro) => erro !== undefined);

  function valoresDoFormulario(): ModalitySettingValues {
    return {
      max_insured_amount: campoParaDinheiro(maxSegurado),
      max_auto_approved_amount: campoParaDinheiro(maxAutomatico),
      min_premium: campoParaDinheiro(premioMinimo),
      min_term_days: campoParaInteiro(vigenciaMinima) ?? null,
      max_term_days: campoParaInteiro(vigenciaMaxima) ?? null,
      max_future_days: campoParaInteiro(diasFuturos) ?? null,
      retroactivity_handling:
        tratativa === '' ? null : (tratativa as RetroactivityHandling),
      max_retroactive_days: campoParaInteiro(diasRetroativos) ?? null,
      auto_cancel_days: campoParaInteiro(diasCancelamentoAutomatico) ?? null,
      manual_cancel_days: campoParaInteiro(diasCancelamentoManual) ?? null,
      modality_rate_bps:
        taxaDaModalidade.trim() === '' ? null : rateToBasisPoints(taxaDaModalidade),
      ...chaves,
    };
  }

  function cadastroDoFormulario(): ModalityPatch {
    return {
      name: nome,
      status: emRascunho ? 'rascunho' : inativa ? 'inativo' : 'ativo',
      insurer_reference_code: referencia,
      code: codigo,
      description: descricao,
      base_rate_bps: rateToBasisPoints(taxaBase || '0'),
      modality_group_id: grupo,
      credit_group: grupoDeCredito,
      credit_reference: referenciaDeCredito,
      susep_process_number: processo,
    };
  }

  const cadastroGravado: ModalityPatch = {
    name: detalhe.name,
    status: detalhe.status,
    insurer_reference_code: detalhe.insurer_reference_code ?? '',
    code: detalhe.code,
    description: detalhe.description,
    base_rate_bps: detalhe.base_rate_bps,
    modality_group_id: detalhe.modality_group_id ?? '',
    credit_group: detalhe.credit_group ?? '',
    credit_reference: detalhe.credit_reference ?? '',
    susep_process_number: detalhe.susep_process_number ?? '',
  };

  function atualizar() {
    void queryClient.invalidateQueries({ queryKey: ['modality', detalhe.id] });
    void queryClient.invalidateQueries({ queryKey: ['modality-settings', detalhe.id] });
    void queryClient.invalidateQueries({
      queryKey: ['entity-history', 'modality', detalhe.id],
    });
    void queryClient.invalidateQueries({ queryKey: ['products'] });
  }

  const salvar = useMutation({
    mutationFn: async () => {
      const cadastro = cadastroDoFormulario();
      let mudou = false;
      if (podeEditarCadastro && !mesmoConteudo(cadastro, cadastroGravado)) {
        await parametrization.updateModality(detalhe.id, cadastro);
        mudou = true;
      }
      const valores = valoresDoFormulario();
      if (podeEditarParametros && !mesmoConteudo(valores, base)) {
        if (rascunho) {
          await parametrization.updateSetting(detalhe.id, rascunho.id, {
            values: valores,
          });
        } else {
          await parametrization.createSetting(detalhe.id, { values: valores });
        }
        mudou = true;
      }
      return mudou;
    },
    onSuccess: (mudou) => {
      toast.success(mudou ? 'Detalhes salvos.' : 'Nada mudou.');
      atualizar();
    },
  });

  function submeter(evento: FormEvent) {
    evento.preventDefault();
    // Os dois campos com asterisco no OnPoint. O rascunho ate poderia ficar sem
    // eles, mas a tela segue o OnPoint, e a versao sem eles nao entra em vigor.
    const obrigatorios: Record<string, string> = {};
    if (diasFuturos.trim() === '')
      obrigatorios.diasFuturos = 'Informe o maximo de dias futuros.';
    if (diasRetroativos.trim() === '') {
      obrigatorios.diasRetroativos =
        'Informe o maximo de dias retroativos automaticos.';
    }
    setFaltando(obrigatorios);
    if (temErro || Object.keys(obrigatorios).length > 0) return;
    salvar.mutate();
  }

  const apiError = salvar.error instanceof ApiError ? salvar.error : null;
  const desligado = somenteLeitura || salvar.isPending;
  const desligadoCadastro = !podeEditarCadastro || salvar.isPending;
  const desligadoParametros = !podeEditarParametros || salvar.isPending;

  const avisos: string[] = [];
  const vMin = campoParaInteiro(vigenciaMinima);
  const vMax = campoParaInteiro(vigenciaMaxima);
  if (typeof vMin === 'number' && typeof vMax === 'number' && vMin > vMax) {
    avisos.push('A vigencia minima esta maior que a maxima.');
  }
  const maxS = campoParaDinheiro(maxSegurado);
  const maxA = campoParaDinheiro(maxAutomatico);
  if (maxS && maxA && maxA.amount > maxS.amount) {
    avisos.push(
      'O valor aprovado automaticamente esta maior que o valor maximo segurado.',
    );
  }

  const atualizadoEm = maisRecente(
    detalhe.updated_at,
    rascunho?.updated_at,
    ativa?.updated_at,
  );

  return (
    <div className="flex flex-col gap-5">
      <SituacaoDaVersao
        ativa={ativa}
        rascunho={rascunho}
        podeAtivar={podeAtivar}
        onAtivar={() => setAtivando(true)}
      />

      <form onSubmit={submeter} noValidate className="flex flex-col gap-5">
        {salvar.error ? (
          <Alert tone="danger" title="Nao foi possivel salvar">
            {salvar.error.message}
          </Alert>
        ) : null}

        <fieldset disabled={desligado} className="flex flex-col gap-4">
          <Field
            label="Nome da modalidade"
            required
            value={nome}
            disabled={desligadoCadastro}
            error={fieldErrorOf(apiError, 'name')}
            onChange={(e) => setNome(e.target.value)}
          />
          <div className="grid gap-4 sm:grid-cols-3">
            <MoneyField
              label="Valor maximo segurado"
              value={maxSegurado}
              disabled={desligadoParametros}
              error={erros.maxSegurado ?? fieldErrorOf(apiError, 'max_insured_amount')}
              onValueChange={setMaxSegurado}
            />
            <MoneyField
              label="Valor maximo aprovado automaticamente"
              value={maxAutomatico}
              disabled={desligadoParametros}
              error={
                erros.maxAutomatico ??
                fieldErrorOf(apiError, 'max_auto_approved_amount')
              }
              onValueChange={setMaxAutomatico}
            />
            <MoneyField
              label="Premio minimo"
              value={premioMinimo}
              disabled={desligadoParametros}
              error={erros.premioMinimo ?? fieldErrorOf(apiError, 'min_premium')}
              onValueChange={setPremioMinimo}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field
              label="Vigencia minima (dias)"
              inputMode="numeric"
              value={vigenciaMinima}
              disabled={desligadoParametros}
              error={erros.vigenciaMinima ?? fieldErrorOf(apiError, 'min_term_days')}
              onChange={(e) => setVigenciaMinima(e.target.value)}
            />
            <Field
              label="Vigencia maxima (dias)"
              inputMode="numeric"
              value={vigenciaMaxima}
              disabled={desligadoParametros}
              error={erros.vigenciaMaxima ?? fieldErrorOf(apiError, 'max_term_days')}
              onChange={(e) => setVigenciaMaxima(e.target.value)}
            />
            <Field
              label="Codigo de referencia da seguradora"
              maxLength={40}
              value={referencia}
              disabled={desligadoCadastro}
              error={fieldErrorOf(apiError, 'insurer_reference_code')}
              onChange={(e) => setReferencia(e.target.value)}
            />
          </div>
          <Field
            label="Maximo de dias futuros"
            required
            inputMode="numeric"
            value={diasFuturos}
            disabled={desligadoParametros}
            error={erros.diasFuturos ?? fieldErrorOf(apiError, 'max_future_days')}
            onChange={(e) => setDiasFuturos(e.target.value)}
          />

          <fieldset className="flex flex-col gap-3 rounded-md border border-line p-4">
            <legend className="px-1 text-xs font-semibold text-ink-700">
              Tratativa de inicio de vigencia
            </legend>
            <div className="flex flex-wrap gap-5">
              <Tratativa
                label="Automatico"
                valor="automatico"
                atual={tratativa}
                name={nomeDaTratativa}
                disabled={desligadoParametros}
                onChange={setTratativa}
              />
              <Tratativa
                label="Esteira"
                valor="esteira"
                atual={tratativa}
                name={nomeDaTratativa}
                disabled={desligadoParametros}
                onChange={setTratativa}
              />
              <Tratativa
                label="Hibrido"
                valor="hibrido"
                atual={tratativa}
                name={nomeDaTratativa}
                disabled={desligadoParametros}
                onChange={setTratativa}
              />
            </div>
            <Field
              label="Maximo de dias retroativos automaticos"
              required
              inputMode="numeric"
              value={diasRetroativos}
              disabled={desligadoParametros}
              error={
                erros.diasRetroativos ?? fieldErrorOf(apiError, 'max_retroactive_days')
              }
              onChange={(e) => setDiasRetroativos(e.target.value)}
            />
          </fieldset>

          <p className="text-xs text-ink-500">
            Atualizado em {atualizadoEm ? formatDate(atualizadoEm) : 'nao informado'}
          </p>

          {emRascunho ? (
            <Alert tone="warning" title="Modalidade em rascunho">
              Ela nao aparece na cotacao. Informe a taxa base, complete os detalhes e
              aprove a versao da configuracao; depois outra pessoa, que nao editou o
              rascunho, ativa a modalidade na lista de modalidades, com justificativa.
            </Alert>
          ) : null}

          <div
            className="rounded-md border border-line px-4"
            role="group"
            aria-label="Interruptores da modalidade"
          >
            <Interruptor
              label="Inativar modalidade"
              checked={inativa}
              disabled={desligadoCadastro || emRascunho}
              onChange={setInativa}
            />
            <Interruptor
              label="Modalidade habilitada para emissao automatica"
              checked={chaves.auto_issuance_enabled}
              disabled={desligadoParametros}
              onChange={alternar('auto_issuance_enabled')}
            />
            <Interruptor
              label="Esta modalidade esta atuando como modalidade complementar"
              checked={chaves.is_complementary}
              disabled={desligadoParametros}
              onChange={alternar('is_complementary')}
            />
            <Interruptor
              label="Esta modalidade permite a inclusao de modalidade complementar"
              checked={chaves.allows_complementary}
              disabled={desligadoParametros}
              onChange={alternar('allows_complementary')}
            />
            <Interruptor
              label="Esta modalidade e restrita para os tomadores com ramos restritos"
              checked={chaves.restricted_for_restricted_activity}
              disabled={desligadoParametros}
              onChange={alternar('restricted_for_restricted_activity')}
            />
            <Interruptor
              label="As vigencias entre as modalidades e cobertura devem ser iguais"
              checked={chaves.equalize_coverage_terms}
              disabled={desligadoParametros}
              onChange={alternar('equalize_coverage_terms')}
            />
            <Interruptor
              label="Permite emissao com segurado Pessoa Fisica"
              checked={chaves.allows_individual_insured}
              disabled={desligadoParametros}
              onChange={alternar('allows_individual_insured')}
            />
            <Interruptor
              label="Permite inclusao de beneficiario"
              checked={chaves.allows_beneficiary}
              disabled={desligadoParametros}
              onChange={alternar('allows_beneficiary')}
            />
            <Interruptor
              label="Esta modalidade e monitorada"
              checked={chaves.is_monitored}
              disabled={desligadoParametros}
              onChange={alternar('is_monitored')}
            />
            <Interruptor
              label="Esta modalidade pode realizar a retomada do objeto (Step-in)"
              checked={chaves.allows_step_in}
              disabled={desligadoParametros}
              onChange={alternar('allows_step_in')}
            />
            <Interruptor
              label="Esta modalidade e de grande risco"
              checked={chaves.is_large_risk}
              disabled={desligadoParametros}
              onChange={alternar('is_large_risk')}
            />
            <Interruptor
              label="Cancelamento automatico de apolices vencidas"
              checked={chaves.auto_cancel_expired}
              disabled={desligadoParametros}
              onChange={alternar('auto_cancel_expired')}
            />
            <Interruptor
              label="Permite de uso de taxa por modalidade"
              checked={chaves.modality_rate_enabled}
              disabled={desligadoParametros}
              onChange={alternar('modality_rate_enabled')}
            />
          </div>

          {avisos.length > 0 ? (
            <Alert tone="warning" title="Confira os valores">
              <ul className="list-disc pl-4">
                {avisos.map((aviso) => (
                  <li key={aviso}>{aviso}</li>
                ))}
              </ul>
              <p className="mt-1">
                A coerencia entre os campos ainda nao e conferida pelo servidor: e regra
                pendente de homologacao.
              </p>
            </Alert>
          ) : null}

          <Secao titulo="Demais parametros da modalidade">
            <p className="text-xs text-ink-500">
              Parametros adicionais da modalidade. Ficam guardados na mesma versao.
            </p>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field
                label="Dias para o cancelamento automatico"
                inputMode="numeric"
                value={diasCancelamentoAutomatico}
                disabled={desligadoParametros}
                error={
                  erros.diasCancelamentoAutomatico ??
                  fieldErrorOf(apiError, 'auto_cancel_days')
                }
                onChange={(e) => setDiasCancelamentoAutomatico(e.target.value)}
              />
              <Field
                label="Dias para o cancelamento manual"
                inputMode="numeric"
                value={diasCancelamentoManual}
                disabled={desligadoParametros}
                error={
                  erros.diasCancelamentoManual ??
                  fieldErrorOf(apiError, 'manual_cancel_days')
                }
                onChange={(e) => setDiasCancelamentoManual(e.target.value)}
              />
              <Field
                label="Taxa da modalidade (%)"
                inputMode="decimal"
                value={taxaDaModalidade}
                disabled={desligadoParametros}
                error={
                  erros.taxaDaModalidade ?? fieldErrorOf(apiError, 'modality_rate_bps')
                }
                onChange={(e) => setTaxaDaModalidade(e.target.value)}
              />
            </div>
            <div className="rounded-md border border-line px-4">
              <Interruptor
                label="Ignorar ramo quando o segurado e pessoa fisica"
                checked={chaves.ignore_branch_for_individual_insured}
                disabled={desligadoParametros}
                onChange={alternar('ignore_branch_for_individual_insured')}
              />
              <Interruptor
                label="Acumulo habilitado para a modalidade"
                checked={chaves.accumulation_enabled}
                disabled={desligadoParametros}
                onChange={alternar('accumulation_enabled')}
              />
              <Interruptor
                label="Esteira para data retroativa habilitada"
                checked={chaves.retroactive_pipe_enabled}
                disabled={desligadoParametros}
                onChange={alternar('retroactive_pipe_enabled')}
              />
              <Interruptor
                label="Modalidade de leiloeiro"
                checked={chaves.is_auctioneer}
                disabled={desligadoParametros}
                onChange={alternar('is_auctioneer')}
              />
            </div>
          </Secao>

          <Secao titulo="Cadastro no SeguraCore">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Codigo"
                required
                value={codigo}
                disabled={desligadoCadastro}
                error={fieldErrorOf(apiError, 'code')}
                onChange={(e) => setCodigo(e.target.value)}
              />
              <Field
                label="Taxa base (%)"
                inputMode="decimal"
                value={taxaBase}
                disabled={desligadoCadastro}
                error={erros.taxaBase ?? fieldErrorOf(apiError, 'base_rate_bps')}
                onChange={(e) => setTaxaBase(e.target.value)}
              />
              <Select
                label="Grupo de modalidades"
                options={[
                  { value: '', label: 'Sem grupo' },
                  ...(grupos.data ?? []).map((g) => ({ value: g.id, label: g.name })),
                ]}
                value={grupo}
                disabled={desligadoCadastro}
                error={fieldErrorOf(apiError, 'modality_group_id')}
                onChange={(e) => setGrupo(e.target.value)}
              />
              <Field
                label="Numero do processo SUSEP"
                maxLength={60}
                value={processo}
                disabled={desligadoCadastro}
                error={fieldErrorOf(apiError, 'susep_process_number')}
                onChange={(e) => setProcesso(e.target.value)}
              />
              <Select
                label="Grupo de credito"
                options={gruposDeCreditoDaModalidade}
                value={grupoDeCredito}
                disabled={desligadoCadastro}
                error={fieldErrorOf(apiError, 'credit_group')}
                onChange={(e) => setGrupoDeCredito(e.target.value)}
              />
              <Select
                label="Referencia de credito"
                options={referenciasDeCredito}
                value={referenciaDeCredito}
                disabled={desligadoCadastro}
                error={fieldErrorOf(apiError, 'credit_reference')}
                onChange={(e) => {
                  setReferenciaDeCredito(e.target.value);
                  const doGrupo = referenciasDeCredito.find(
                    (r) => r.value === e.target.value,
                  )?.grupo;
                  if (doGrupo) setGrupoDeCredito(doGrupo);
                }}
              />
            </div>
            <Textarea
              label="Descricao"
              value={descricao}
              disabled={desligadoCadastro}
              onChange={(e) => setDescricao(e.target.value)}
            />
          </Secao>
        </fieldset>

        {!somenteLeitura ? (
          <div className="flex justify-end">
            <Button type="submit" loading={salvar.isPending} disabled={temErro}>
              Salvar detalhes
            </Button>
          </div>
        ) : null}
      </form>

      {/* Nome da cobertura impresso no documento da apolice (P-MIN-2). */}
      <NomeDaCoberturaImpresso productId={detalhe.product.id} modalityId={detalhe.id} />

      <VersoesDaConfiguracao modalityId={detalhe.id} />

      {rascunho ? (
        <AtivarVersao
          aberto={ativando}
          modalityId={detalhe.id}
          rascunho={rascunho}
          onFechar={() => setAtivando(false)}
          onAtivada={() => {
            setAtivando(false);
            toast.success(`Versao ${rascunho.version} ativada.`);
            atualizar();
          }}
        />
      ) : null}
    </div>
  );
}

function Secao({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section
      className="flex flex-col gap-4 border-t border-line pt-4"
      aria-label={titulo}
    >
      <h4 className="text-xs font-semibold uppercase tracking-wide text-ink-500">
        {titulo}
      </h4>
      {children}
    </section>
  );
}

function SituacaoDaVersao({
  ativa,
  rascunho,
  podeAtivar,
  onAtivar,
}: {
  ativa: ModalitySetting | null;
  rascunho: ModalitySetting | null;
  podeAtivar: boolean;
  onAtivar: () => void;
}) {
  const autoria = autoriaDe(rascunho?.authored_by_you, rascunho?.may_self_approve);
  return (
    <Alert tone="info" title="Configuracao versionada">
      <div className="flex flex-col gap-2">
        <p>
          Os valores ficam guardados como parametro versionado. Nenhuma regra decide por
          eles ate a homologacao: o motor de credito e a cotacao continuam usando os
          parametros de hoje.
        </p>
        <p>
          {ativa
            ? `Em vigor: versao ${ativa.version}, ativada por ${ativa.activated_label || 'nao informado'} em ${formatDateTime(ativa.activated_at)}.`
            : 'Nenhuma versao em vigor.'}{' '}
          {rascunho
            ? `Rascunho aberto: versao ${rascunho.version}, por ${rascunho.created_label || 'carga do sistema'}. Salvar detalhes edita este rascunho.`
            : 'Salvar detalhes abre um rascunho com os valores da tela.'}
        </p>
        {rascunho && podeAtivar ? (
          <div className="flex flex-wrap items-center gap-3">
            <Button size="sm" onClick={onAtivar} disabled={autoria.barrada}>
              Ativar versao {rascunho.version}
            </Button>
            {autoria.barrada ? (
              <span className="text-2xs">
                Voce abriu ou editou este rascunho. Outra pessoa com a alcada ativa a
                versao.
              </span>
            ) : null}
            {autoria.autoaprovacao ? (
              <span className="text-2xs">{AVISO_AUTOAPROVACAO}</span>
            ) : null}
          </div>
        ) : null}
      </div>
    </Alert>
  );
}

function AtivarVersao({
  aberto,
  modalityId,
  rascunho,
  onFechar,
  onAtivada,
}: {
  aberto: boolean;
  modalityId: string;
  rascunho: ModalitySetting;
  onFechar: () => void;
  onAtivada: () => void;
}) {
  const [justificativa, setJustificativa] = useState('');
  const ativar = useMutation({
    mutationFn: () =>
      parametrization.activateSetting(modalityId, rascunho.id, justificativa),
    onSuccess: () => {
      setJustificativa('');
      onAtivada();
    },
  });
  const apiError = ativar.error instanceof ApiError ? ativar.error : null;

  return (
    <Modal
      open={aberto}
      onClose={onFechar}
      title={`Ativar versao ${rascunho.version}`}
      description="A versao em vigor passa a revogada, e esta entra em vigor a partir de agora."
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(evento) => {
          evento.preventDefault();
          ativar.mutate();
        }}
      >
        {ativar.error && !fieldErrorOf(apiError, 'justification') ? (
          <Alert tone="danger" title="Nao foi possivel ativar">
            {ativar.error.message}
          </Alert>
        ) : null}
        <Textarea
          label="Justificativa"
          required
          value={justificativa}
          error={fieldErrorOf(apiError, 'justification')}
          onChange={(e) => setJustificativa(e.target.value)}
        />
        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button type="button" variant="secondary" onClick={onFechar}>
            Fechar
          </Button>
          <Button
            type="submit"
            loading={ativar.isPending}
            disabled={justificativa.trim() === ''}
          >
            Ativar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function VersoesDaConfiguracao({ modalityId }: { modalityId: string }) {
  const consulta = useQuery({
    queryKey: ['modality-settings', modalityId],
    queryFn: ({ signal }) => parametrization.listSettings(modalityId, signal),
  });
  return (
    <DetailCard
      title="Versoes da configuracao"
      description="Toda versao fica guardada: o rascunho, a ativa e as revogadas."
      loading={consulta.isPending}
      error={consulta.error}
    >
      <DataTable
        columns={[
          {
            key: 'versao',
            header: 'Versao',
            render: (v: ModalitySetting) => `Versao ${v.version}`,
          },
          {
            key: 'situacao',
            header: 'Situacao',
            render: (v: ModalitySetting) => (
              <StatusBadge kind="versao" value={v.status} />
            ),
          },
          {
            key: 'aberta',
            header: 'Aberta por',
            render: (v: ModalitySetting) => v.created_label || 'carga do sistema',
          },
          {
            key: 'ativada',
            header: 'Ativada por',
            render: (v: ModalitySetting) =>
              v.activated_label
                ? `${v.activated_label} em ${formatDateTime(v.activated_at)}`
                : '-',
          },
          {
            key: 'motivo',
            header: 'Justificativa',
            render: (v: ModalitySetting) =>
              [v.justification, v.revocation_reason].filter(Boolean).join(' . ') || '-',
          },
        ]}
        items={consulta.data ?? []}
        keyOf={(v) => v.id}
        empty={
          <EmptyState icon="layers" title="Nenhuma versao da configuracao ainda" />
        }
      />
    </DetailCard>
  );
}
